import React, { useEffect, useState } from "react";
import { Loader2, Building2, Users } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

function ModeOption({ value, mode, setMode, icon: Icon, title, text }) {
  const on = mode === value;
  return (
    <button type="button" onClick={() => setMode(value)} data-testid={`approve-mode-${value}`}
      className={`w-full text-left flex gap-3 rounded-xl border p-3 transition-colors ${on ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}>
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${on ? "text-primary" : "text-slate-400"}`} />
      <span><span className="block font-semibold text-sm">{title}</span><span className="block text-xs text-slate-500">{text}</span></span>
    </button>
  );
}

// Approve dialog: own new company (admin) or join an existing company with a role.
export default function ApproveAccessDialog({ request, onClose, onConfirm, busy }) {
  const [mode, setMode] = useState("own");
  const [list, setList] = useState(null);
  const [ws, setWs] = useState("");
  const [role, setRole] = useState("user");
  useEffect(() => {
    if (!request) return;
    setMode("own"); setWs(""); setRole("user");
    api.get("/access-requests/workspaces").then((r) => setList(r.data)).catch(() => setList([]));
  }, [request]);
  const roles = (list || []).find((w) => w.id === ws)?.roles || [];
  const canSave = mode === "own" || (ws && role);

  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="approve-access-dialog">
        <DialogHeader>
          <DialogTitle>Zugang freigeben</DialogTitle>
          <DialogDescription>{request?.name} · {request?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <ModeOption value="own" mode={mode} setMode={setMode} icon={Building2} title="Eigene Firma"
            text="Neue, leere Firma – der Nutzer ist dort Administrator." />
          <ModeOption value="join" mode={mode} setMode={setMode} icon={Users} title="Bestehender Firma beitreten"
            text="Der Nutzer arbeitet als Mitarbeiter in einer vorhandenen Firma." />
        </div>
        {mode === "join" && (
          <div className="space-y-3 pt-1">
            {list === null ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
              <Select value={ws} onValueChange={(v) => { setWs(v); setRole("user"); }}>
                <SelectTrigger data-testid="approve-workspace-select"><SelectValue placeholder="Firma wählen" /></SelectTrigger>
                <SelectContent>
                  {list.map((w) => <SelectItem key={w.id} value={w.id} data-testid={`approve-workspace-${w.id}`}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {ws && (
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="approve-role-select"><SelectValue placeholder="Rolle wählen" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`approve-role-${r.key}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        <Button className="w-full h-11 bg-green-600 hover:bg-green-700 mt-2" disabled={!canSave || busy} data-testid="approve-access-confirm"
          onClick={() => onConfirm(mode === "own" ? { mode } : { mode, workspace_id: ws, role })}>
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Jetzt freigeben
        </Button>
      </DialogContent>
    </Dialog>
  );
}
